(ns hai.library.connections.create
  (:require
   [cljs.reader]
   [clojure.string :as str]
   [goog.crypt.base64 :as base64]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e defevent-fx defsub]]
   [hai.web-app.components.form :as form]
   [hai.web-app.layout :as layout]
   [hai.web-app.platform]
   [hai.web-app.util.fetch :as fetch]
   [kitchen-async.promise :as p]
   [lambdaisland.ornament :as o]
   [lambdaisland.uri :as uri]
   [reagent.core :as r])
  (:import
   (goog.crypt Sha256)))

(defn- byte-array [x]
  (cond
    (or (string? x)
        (coll? x)
        (keyword? x)
        (number? x)
        (boolean? x))
    (.encode (js/TextEncoder.) x)

    :else
    x))

(defn base64-uri-encode
  "URI-safe base64 encoding, use -_ instead of +/, and don't pad with ="
  [x]
  (base64/encodeByteArray
   (byte-array x)
   base64/Alphabet.WEBSAFE_NO_PADDING))

(defn sha256-hash [bytes]
  (let [bytes (if (string? bytes) (.encode (js/TextEncoder.) bytes) bytes)
        digest (.digest (doto (Sha256.) (.update bytes)))]
    digest))

(defn random-string [len]
  (base64-uri-encode (js/crypto.getRandomValues (js/Uint8Array. len))))

(defn start-humble-auth!
  [method & {:keys [response-type scope redirect-uri metadata]}]
  (let [verifier  (random-string 60)
        challenge (base64-uri-encode (sha256-hash verifier))
        state     (random-string 32)]
    (uri/assoc-query*
     "/oauth/v1/authorize"
     (cond-> {:client_id             "humble-platform"
              :redirect_uri          redirect-uri
              :response_type         response-type
              :code_challenge        challenge
              :code_challenge_method "S256"
              :auth_method           method
              :state                 state}
       scope    (assoc :scope scope)
       metadata (assoc :metadata (pr-str metadata))))))

(o/defstyled app-block :div
  :flex :flex-col :items-center :text-center :p-4
  ([{:keys [icon text]}]
   [:<> icon [:span text]]))

(defn redirect-uri
  [provider]
  (str hai.web-app.platform/origin "/app/admin/connections/oauth/callback/" provider))

(defn hubspot-oauth-url
  []
  (str
   (start-humble-auth! "hubspot" {:redirect-uri (redirect-uri "hubspot")
                                  :response-type "authorization_voucher"
                                  :scope "crm.objects.companies.read crm.objects.contacts.read crm.objects.deals.read crm.schemas.contacts.read"})))

(o/defstyled hubspot :div
  :flex :flex-col :gap-4 :mt-4
  ([_entity _error?]
   [:<>
    [c/Link
     {:href (hubspot-oauth-url) :target "_top"}
     [c/Button "Connect to Hubspot"]]
    [c/CalloutRoot
     {:color "blue"}
     [c/CalloutText
      [c/Text
       {:size "2"}
       "You will be redirected to Hubspot to provide Humble with the access. After that you'll be redirected back to Humble."]]]]))

(defn notion-oauth-url
  []
  (str
   (start-humble-auth! "notion" {:redirect-uri (redirect-uri "notion")
                                 :response-type "authorization_voucher"})))

(defn linear-oauth-url
  ([] (linear-oauth-url nil))
  ([metadata]
   (str
    (start-humble-auth! "linear" {:redirect-uri (redirect-uri "linear")
                                  :response-type "authorization_voucher"
                                  :scope "read,write,issues:create,admin"
                                  :metadata metadata}))))

(def gcal-default-scopes
  "openid email https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/calendar.events.readonly")

(def gcal-contact-backfill-scopes
  "Elevated scope set requested when developer-features flag is on. Adds People
  API scopes so the post-sync enrichment job can resolve attendee emails to real
  names. Sensitive scopes — only test users in the Google Cloud project will be
  able to grant them while consent screen is in Testing mode."
  (str gcal-default-scopes
       " https://www.googleapis.com/auth/directory.readonly"
       " https://www.googleapis.com/auth/contacts.other.readonly"))

(defn google-calendar-oauth-url
  ([] (google-calendar-oauth-url nil))
  ([metadata]
   (let [dev? @(<s [:feature-flag/developer-features?])]
     (str
      (start-humble-auth! "google-calendar"
                          {:redirect-uri  (redirect-uri "google-calendar")
                           :response-type "authorization_voucher"
                           :scope         (if dev?
                                            gcal-contact-backfill-scopes
                                            gcal-default-scopes)
                           :metadata      metadata})))))

(def outlook-calendar-scopes
  "openid email Calendars.Read offline_access")

(defn outlook-calendar-oauth-url
  ([] (outlook-calendar-oauth-url nil))
  ([metadata]
   (str
    (start-humble-auth! "outlook-calendar"
                        {:redirect-uri  (redirect-uri "outlook-calendar")
                         :response-type "authorization_voucher"
                         :scope         outlook-calendar-scopes
                         :metadata      metadata}))))

(defn github-oauth-url
  ([] (github-oauth-url nil))
  ([metadata]
   (str
    (start-humble-auth! "github" {:redirect-uri (redirect-uri "github")
                                  :response-type "authorization_voucher"
                                  :metadata metadata}))))

(defevent-fx :connection/voucher-error
  [_ {:keys [source-type body]}]
  (let [scope-insufficient? (contains? #{:scope-insufficient "scope-insufficient"} (:error body))]
    {:fx [[:dispatch [:toast/error
                      {:text (if scope-insufficient?
                               (str "Couldn't connect " (name source-type)
                                    " — the provider didn't grant calendar access. Reconnect and"
                                    " approve the calendar permission.")
                               (str "Couldn't connect " (name source-type)
                                    " — it may already be connected."))}]]
          [:dispatch [:routing/navigate :connections/table]]]}))

(defevent-fx :connection/create-from-voucher
  [_ props]
  (let [frontend-action (:frontend-action props)
        source-type (:connection/type props)]
    {:fetch {:url "/connections/v1"
             :method :put
             :body {:connection/uuid (:connection/id props)
                    :connection/type source-type
                    :oauth.state.authorization-voucher/uuid (:authorization-token/id props)}
             :on-success-fx [[:dispatch (or frontend-action [:routing/navigate :connections/table])]]
             :on-error-fx [[:dispatch [:connection/voucher-error
                                       {:source-type source-type :body '?body}]]]}}))

(defevent-fx :oauth/handle-callback
  [{:keys [db]} {:keys [source-type authorization-token metadata]}]
  (if (some? authorization-token)
    (let [parsed-metadata (when metadata (cljs.reader/read-string metadata))]
      {:db db
       :fx [[:dispatch [:connection/create-from-voucher
                        (cond-> {:connection/id (random-uuid)
                                 :connection/type (keyword source-type)
                                 :authorization-token/id (parse-uuid authorization-token)}
                          (:frontend-action parsed-metadata)
                          (assoc :frontend-action (:frontend-action parsed-metadata)))]]]})
    {:db db
     :fx [[:dispatch [:toast/error {:text (str "OAuth authorization failed for " source-type ". Check server logs.")}]]
          [:dispatch [:routing/navigate :connections/table]]]}))

(o/defstyled notion :div
  :flex :flex-col :gap-4 :mt-4
  ([]
   [:<>
    [c/Link
     {:href (notion-oauth-url) :target "_top"}
     [c/Button "Connect to Notion"]]
    [c/CalloutRoot
     {:color "blue"}
     [c/CalloutText
      [c/Text
       {:size "2"}
       "You will be redirected to Notion to provide Humble with access to the required databases. After that you'll be redirected back to Humble."]]]]))

(o/defstyled linear :div
  :flex :flex-col :gap-4 :mt-4
  ([]
   [:<>
    [c/Link
     {:href (linear-oauth-url) :target "_top"}
     [c/Button "Connect to Linear"]]
    [c/CalloutRoot
     {:color "blue"}
     [c/CalloutText
      [c/Text
       {:size "2"}
       "You will be redirected to Linear to authorize Humble. After authorization, you'll be redirected back to Humble."]]]]))

(o/defstyled google-calendar :div
  :flex :flex-col :gap-4 :mt-4
  ([]
   [:<>
    [c/Link
     {:href (google-calendar-oauth-url) :target "_top"}
     [c/Button "Connect to Google Calendar"]]
    [c/CalloutRoot
     {:color "blue"}
     [c/CalloutText
      [c/Text
       {:size "2"}
       "You will be redirected to Google to grant Humble read-only access to your calendar. After authorization, you'll be redirected back to Humble."]]]]))

(o/defstyled outlook-calendar :div
  :flex :flex-col :gap-4 :mt-4
  ([]
   [:<>
    [c/Link
     {:href (outlook-calendar-oauth-url) :target "_top"}
     [c/Button "Connect to Outlook Calendar"]]
    [c/CalloutRoot
     {:color "blue"}
     [c/CalloutText
      [c/Text
       {:size "2"}
       "You will be redirected to Microsoft to grant Humble read-only access to your calendar. After authorization, you'll be redirected back to Humble."]]]]))

(o/defstyled github :div
  :flex :flex-col :gap-4 :mt-4
  ([]
   [:<>
    [c/Link
     {:href (github-oauth-url) :target "_top"}
     [c/Button "Connect to GitHub"]]
    [c/CalloutRoot
     {:color "blue"}
     [c/CalloutText
      [c/Text
       {:size "2"}
       "You will be redirected to GitHub to authorize Humble. After authorization, you'll be redirected back to Humble."]]]]))

(defn required-scopes
  []
  [:<>
   "Ensure that all 4 scopes "
   [c/Code "data.records:read"] ", "
   [c/Code "data.records:write"] ", "
   [c/Code "user.email:read"]
   " and "
   [c/Code "schema.bases:read"]
   " are selected."])

(defn airtable
  []
  (r/with-let [in-flight? (r/atom false)
               entity (r/atom {})
               error? (r/atom nil)]
    [form/form
     [form/text
      {:title "Description"
       :attr :connection/description
       :entity entity}]
     [form/text
      {:title "Token"
       :attr :token-conn/token
       :entity entity}]
     (if-not @error?
       [c/CalloutRoot
        {:color "blue"}
        [c/CalloutText
         [c/Text
          {:size "4"
           :weight "bold"}
          "Create your "
          [c/Link {:href "https://airtable.com/create/tokens/new"
                   :target "_blank"}
           "personal access token"]]]
        [c/CalloutText [required-scopes]]]
       [c/CalloutRoot
        {:color "crimson"}
        [c/CalloutText
         [c/Text
          {:size "4"
           :weight "bold"}
          "Setup incomplete"]
         [:ul
          [:li "Ensure that you are using a "
           [:strong "Personal Access Token"]
           ", not a "
           [:strong "Token ID."]]
          [:li "Please " [:strong "Add a Base"] " to establish a connection and access your records."]
          [:li [required-scopes]]
          [:li "Personal Access Token is available only once, at creation. If you have already created, you may need to 'regenerate' the token."]
          [:li "If an existing token was regenerated, it can invalidate any connections with the previous tokens. Choose a different connection, or create a new one."]]]])
     [:div.buttons
      [c/Button
       {:disabled (or (-> @entity :token-conn/token empty?)
                      @in-flight?)
        :on-click (fn [_]
                    (reset! in-flight? true)
                    (let [uuid (random-uuid)]
                      (p/let [resp (fetch/put "/connections/v1"
                                              {:body (assoc @entity
                                                            :connection/uuid uuid
                                                            :connection/type :airtable)})]
                        (if (= 200 (:status resp))
                          (>e [:routing/navigate [:connections/table]])
                          (do
                            (reset! in-flight? false)
                            (reset! error? true))))))
        :size "2"}
       "Create"]
      [form/cancel
       {:in-flight? in-flight?
        :on-click (fn [_] (>e [:routing/navigate [:connections/table]]))}]]]))

(o/defstyled database :div
  :flex :flex-col :gap-4 :mt-4
  ([]
   (r/with-let [in-flight? (r/atom false)
                entity (r/atom {})
                error? (r/atom nil)]
     [form/form
      [form/text
       {:title "Description"
        :attr :connection/description
        :entity entity}]
      [form/text
       {:title "Connection URL"
        :attr :db-conn/url
        :entity entity}]
      (when @error?
        [c/CalloutRoot
         {:color "crimson"}
         [c/CalloutText
          [c/Text
           {:size "4"
            :weight "bold"}
           "Failed to create the connection"]]])
      [:div.buttons
       [c/Button
        {:disabled (or (-> @entity :db-conn/url empty?)
                       @in-flight?)
         :on-click (fn [_]
                     (reset! in-flight? true)
                     (let [uuid (random-uuid)]
                       ;;TODO (evgheni): this should be an event goddammit! (same for airtable)
                       (p/let [resp (fetch/put "/connections/v1"
                                               {:body (assoc @entity
                                                             :connection/uuid uuid
                                                             :connection/type :database)})]
                         (if (= 200 (:status resp))
                           (>e [:routing/navigate [:connections/table]])
                           (do
                             (reset! in-flight? false)
                             (reset! error? true))))))
         :size "2"}
        "Create"]
       [form/cancel
        {:in-flight? in-flight?
         :on-click (fn [_] (>e [:routing/navigate [:connections/table]]))}]]])))

(defsub ::source-type
  [db]
  (keyword (get-in db [:routing/current-route :path-params :source/type])))

(def source-options
  [{:source/title "airtable"
    :source/icon icons/airtable}
   {:source/title "notion"
    :source/icon icons/notion}
   {:source/title "hubspot"
    :source/icon icons/hubspot}
   {:source/title "linear"
    :source/icon icons/linear}
   {:source/title "google-calendar"
    :source/label "Google Calendar"
    :source/icon icons/google-calendar}
   {:source/title "outlook-calendar"
    :source/label "Outlook Calendar"
    :source/icon icons/outlook-calendar}
   {:source/title "github"
    :source/label "GitHub"
    :source/icon icons/github}
   {:source/title "database"
    :source/icon icons/database-generic}])

(defn source-types
  []
  (into
   [c/RadioCardsRoot
    {:columns "repeat(auto-fit, 160px)"
     :defaultValue @(<s [::source-type])}]
   (map
    (fn [{:source/keys [title label icon]}]
      [c/RadioCardsItem
       {:size "4"
        :value (keyword title)
        :on-click #(>e [:routing/navigate [:connections/create {:source/type title}]])}
       [app-block {:text (or label (str/capitalize title)) :icon [icon {:size 24}]}]])
    source-options)))

(o/defstyled form form/form
  ([]
   (let [source-type @(<s [::source-type])]
     [:<>
      [source-types]
      (case source-type
        :airtable [airtable]
        :hubspot [hubspot]
        :notion [notion]
        :linear [linear]
        :google-calendar [google-calendar]
        :outlook-calendar [outlook-calendar]
        :github [github]
        :database [database]
        nil)])))

(defn page
  [_]
  (let [{{source :source/type} :path-params} @(<s :routing/current-route)
        ;; NOTE source is `nil` in the `:connections/new` route and in that case the `layout/*`
        ;; components will use the `:label` defined in the route. On the `:connections/create` route
        ;; source is the connection type (e.g. `"airtable"`).
        source-label (some-> source str/capitalize)
        heading-label (when source-label
                        (str "New " source-label " Connection"))]
    [layout/page
     [layout/breadcrumbs
      {:breadcrumbs (and source-label [source-label])}]
     [layout/heading {:label heading-label}]
     [layout/content
      [form]]]))
